import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// ─── REQUEST INTERCEPTOR — attach JWT ────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auxtax_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── RESPONSE INTERCEPTOR — handle 401 ───────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const code = error.response?.data?.code;
      if (code === 'TOKEN_EXPIRED' || error.response?.data?.message === 'User no longer exists') {
        localStorage.removeItem('auxtax_token');
        localStorage.removeItem('auxtax_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ─── AUTH ─────────────────────────────────────────────────────────────────────
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// ─── TAX RETURNS ──────────────────────────────────────────────────────────────
export const taxAPI = {
  list: () => api.get('/tax-returns'),
  get: (id) => api.get(`/tax-returns/${id}`),
  create: (data) => api.post('/tax-returns', data),
  update: (id, data) => api.put(`/tax-returns/${id}`, data),
  delete: (id) => api.delete(`/tax-returns/${id}`),
  calculate: (data) => api.post('/tax-returns/calculate', data),
  lodge: (id) => api.post(`/tax-returns/${id}/lodge`),
  dashboard: () => api.get('/tax-returns/summary/dashboard'),
};

// ─── SUPERANNUATION ───────────────────────────────────────────────────────────
export const superAPI = {
  list: (params) => api.get('/superannuation', { params }),
  get: (id) => api.get(`/superannuation/${id}`),
  create: (data) => api.post('/superannuation', data),
  update: (id, data) => api.put(`/superannuation/${id}`, data),
  delete: (id) => api.delete(`/superannuation/${id}`),
  calculateObligation: (data) => api.post('/superannuation/calculate/obligation', data),
  checkCap: (data) => api.post('/superannuation/calculate/cap-check', data),
  summary: () => api.get('/superannuation/summary/overview'),
};

// ─── PROPERTIES ───────────────────────────────────────────────────────────────
export const propertyAPI = {
  list: (params) => api.get('/properties', { params }),
  get: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
  calculateRental: (data) => api.post('/properties/calculate/rental', data),
  calculateCGT: (data) => api.post('/properties/calculate/cgt', data),
  portfolio: () => api.get('/properties/summary/portfolio'),
};

// ─── REFERENCE DATA ───────────────────────────────────────────────────────────
export const referenceAPI = {
  taxRates: () => api.get('/reference/tax-rates'),
  states: () => api.get('/reference/states'),
  financialYears: () => api.get('/reference/financial-years'),
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0.00';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(amount);
};

export const formatPercent = (value) => {
  if (value === null || value === undefined) return '0%';
  return `${value.toFixed(2)}%`;
};

export const getErrorMessage = (error) => {
  if (error.response?.data?.message) return error.response.data.message;
  if (error.response?.data?.errors) {
    return error.response.data.errors.map(e => e.msg).join(', ');
  }
  return error.message || 'An unexpected error occurred';
};

export default api;